import { motion } from 'motion/react'
import { onboarding } from '@/lib/motion/presets'
import { useLaunchTransition } from '@/stores/launchTransition'

// Radius that guarantees the circle covers the whole viewport from `origin`:
// the distance to the farthest corner.
function coveringRadius(x: number, y: number) {
  const w = window.innerWidth
  const h = window.innerHeight

  const dx = Math.max(x, w - x)
  const dy = Math.max(y, h - y)

  return Math.ceil(Math.hypot(dx, dy))
}

/**
 * App-level outro cover for the onboarding finish. Mounted OUTSIDE the router
 * so it persists across the /onboarding → /chat-builder swap: the circle
 * expands from the Finish button, the route (and any guard redirect) changes
 * invisibly underneath, then the cover wipes up to reveal the builder.
 */
export function LaunchTransitionOverlay() {
  const phase = useLaunchTransition((s) => s.phase)
  const origin = useLaunchTransition((s) => s.origin)
  const presentation = useLaunchTransition((s) => s.presentation)
  const markCovered = useLaunchTransition((s) => s.markCovered)
  const reset = useLaunchTransition((s) => s.reset)

  if (phase === 'idle' || !origin) return null

  const radius = coveringRadius(origin.x, origin.y)
  const at = `at ${origin.x}px ${origin.y}px`

  return (
    <motion.div
      data-testid="launch-transition-overlay"
      initial={{ clipPath: `circle(0px ${at})`, y: 0 }}
      animate={
        phase === 'revealing'
          ? { clipPath: `circle(${radius}px ${at})`, y: '-100%' }
          : { clipPath: `circle(${radius}px ${at})`, y: 0 }
      }
      transition={
        phase === 'revealing' ? onboarding.outro.reveal : onboarding.outro.cover
      }
      onAnimationComplete={() => {
        if (phase === 'covering') markCovered()
        if (phase === 'revealing') reset()
      }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-primary"
    >
      {presentation === 'aeo' && (
        <div
          aria-hidden="true"
          data-testid="aeo-signal-rings"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <span
            className="absolute size-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary-foreground/35"
            style={{ left: origin.x, top: origin.y }}
          />
          <span
            className="absolute size-48 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary-foreground/20"
            style={{ left: origin.x, top: origin.y }}
          />
          <span
            className="absolute size-80 -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary-foreground/10"
            style={{ left: origin.x, top: origin.y }}
          />
        </div>
      )}
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: phase === 'revealing' ? 0 : 1, y: 0 }}
        transition={{
          ...onboarding.outro.caption,
          delay: phase === 'covering' ? onboarding.outro.captionDelay : 0,
        }}
        className="text-lg font-medium text-primary-foreground"
      >
        {presentation === 'aeo'
          ? 'Opening your first visibility readout…'
          : 'Launching your agent…'}
      </motion.p>
    </motion.div>
  )
}
